/**
 * What a failed enrolment or capture upload means for the enrol sheet (slice 04). A retry
 * resends the same ids and the same content, so it is offered only where sending again can
 * help: no answer, a timeout, a service or audit outage, or bytes that arrived damaged.
 */
import type { EnrolProblemView } from '@/components/humint/check/EnrolPanelStatus'
import { ApiError } from '@/services/api/client'
import type { CaptureRefusalCode, EncounterRefusalCode } from '@/services/api/humint'
import { classifySaveError, detailCode, type SaveFailure } from './errors'

/**
 * The sheet's words for a failure (`classifySaveError` reads the shared ones): the capture
 * refusals are the 422 `detail.code`s, `schema_missing_encounter` is the case whose schema
 * cannot hold an encounter and `encounter_not_in_case` a kept encounter the case no longer
 * holds. A 503 `encounter_unavailable` (the photo is stored, its encounter link is not) is an
 * outage like any other: the retry resends the same capture id.
 */
export type EnrolProblem = EnrolProblemView

export type EnrolFailure = SaveFailure<EnrolProblem>

const RETRYABLE: ReadonlySet<EnrolProblem> = new Set<EnrolProblem>([
  'offline',
  'timeout',
  'error',
  'audit_unavailable',
  'sha256_mismatch',
])

export function isRetryable(problem: EnrolProblem): boolean {
  return RETRYABLE.has(problem)
}

/**
 * The photo itself was refused (too large, not a JPEG): not an outage, and sending the same
 * bytes again cannot help. The operator takes a new photo.
 */
const RETAKE: ReadonlySet<EnrolProblem> = new Set<EnrolProblem>([
  'capture_too_large',
  'unsupported_format',
])

export function needsRetake(problem: EnrolProblem): boolean {
  return RETAKE.has(problem)
}

/**
 * The enrol call was refused as sent and nothing was created (slice 06): a tag another subject
 * of the chosen encounter holds, an encounter the case no longer has, a value the encounter's
 * type does not take, a time out of range. The operator fixes the form and taps again, with the
 * same ids.
 */
const EDITABLE: ReadonlySet<EnrolProblem> = new Set<EnrolProblem>([
  'duplicate_tag',
  'encounter_not_in_case',
  'rejected',
  'time_out_of_range',
])

export function isEditable(problem: EnrolProblem): boolean {
  return EDITABLE.has(problem)
}

/**
 * The 422 `detail.code`s the sheet has words for. `encounter_ambiguous` (both a new encounter
 * and a kept one) is a client fault the operator cannot fix, so it reads as `rejected`.
 */
const REFUSAL_PROBLEM: Readonly<Record<CaptureRefusalCode | EncounterRefusalCode, EnrolProblem>> = {
  subject_not_in_case: 'subject_not_in_case',
  sha256_mismatch: 'sha256_mismatch',
  unsupported_kind: 'unsupported_kind',
  capture_too_large: 'capture_too_large',
  unsupported_format: 'unsupported_format',
  case_schema_missing_encounter: 'schema_missing_encounter',
  encounter_not_in_case: 'encounter_not_in_case',
  encounter_ambiguous: 'rejected',
  duplicate_tag_in_encounter: 'duplicate_tag',
  detention_only_values: 'rejected',
  occurred_at_out_of_range: 'time_out_of_range',
}

/** Classify a failed enrol or capture call (see `classifySaveError`). */
export function classifyEnrolError(error: unknown, online: boolean): EnrolFailure {
  // Keep the checked photo and frozen ids for a retry if the case is restored.
  if (error instanceof ApiError && error.status === 404 && detailCode(error) === 'case_not_found') {
    return { kind: 'problem', problem: 'error' }
  }
  return classifySaveError(error, online, { byCode: REFUSAL_PROBLEM })
}
